/* eslint-disable react/prop-types */

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Mousetrap from 'mousetrap';

import { TableVirtuoso } from 'react-virtuoso';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import CopyToClipboard from '../CopyToClipboard';
import Loader from '../../components/Loader';
import LoaderSkeleton from '../LoaderSkeleton';
import OverlayTriggerTooltip from '../OverlayTriggerTooltip';

import { useScrollSync, useOutsideClick } from '../../helpers';

import SortIndicator from '../SortIndicator';

import {
  copySelectedCellValues,
  getCopyTableValue,
  getCellDown,
  getCellLeft,
  getCellRight,
  getCellUp,
  getSelectedCellRange,
  sortTypes,
  transformColumns,
} from './utilities/helpers';

import styles from './_index.module.scss';

/**
 * @typedef {Object} TableData
 * @property {import('./utilities/types').TableDataColumn[]} columns
 * @property {import('./utilities/types').TableDataShape['data']} data
 */

/**
 * @typedef {Object} TableDataProps
 * @property {import('react').ReactNode[]} [actionButtons]
 * @property {import('./utilities/types').TableDataColumn[]} columns
 * @property {boolean} [copyEnabled]
 * @property {import('./utilities/types').TableDataShape['data']} data
 * @property {boolean} [hasPlaceholders]
 * @property {boolean} [isLoading]
 * @property {boolean} [isUpdating]
 * @property {import('react').ReactNode} [prescriptText]
 * @property {() => import('react').ReactNode} [renderEmptyMessage]
 * @property {number} [rowHeight]
 * @property {import('react').Dispatch<import('react').SetStateAction<any>>} [setDownloadData]
 * @property {boolean} [shouldTruncateHeaders]
 * @property {number} [size]
 * @property {boolean} [stickyHeader]
 * @property {boolean} [useWindowScroll]
 */

/**
 * @param {TableDataProps} props
 */
const TableData = ({
  actionButtons = [],
  columns,
  copyEnabled = true,
  data,
  hasPlaceholders = false,
  isLoading,
  isUpdating,
  prescriptText,
  renderEmptyMessage,
  rowHeight = 30,
  setDownloadData,
  shouldTruncateHeaders = true,
  size,
  stickyHeader = false,
  useWindowScroll = true,
}) => {
  const tableId = useId();
  const tableComponents = useMemo(() => createTableComponents(tableId), [tableId]);
  const tableRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [sorting, setSorting] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Transform columns from react-table v7 format to TanStack v8 format
  const transformedColumns = useMemo(() => transformColumns(columns), [columns]);

  const table = useReactTable({
    columns: transformedColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    sortingFns: sortTypes,
    state: { sorting },
  });

  const rows = table.getRowModel().rows;
  const rowCount = rows.length;

  const [selectedCell, setSelectedCell] = useState(null); // for range selection
  const [rangeEndCell, setRangeEndCell] = useState(null);

  const clearSelectedCells = () => {
    setSelectedCell(null);
    setRangeEndCell(null);
  };

  useEffect(() => {
    // keyboard select
    if (selectedCell) {
      Mousetrap.bind(['up'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellUp({ selectedCell, table }));
      });
      Mousetrap.bind(['down'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellDown({ selectedCell, table }));
      });
      Mousetrap.bind(['left'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellLeft({ selectedCell, table }));
      });
      Mousetrap.bind(['right'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellRight({ selectedCell, table }));
      });
    }
  }, [selectedCell, table]);

  useEffect(() => {
    // copy-to-clipboard
    if (selectedCell || rangeEndCell) {
      Mousetrap.bind(['ctrl+c', 'command+c'], () => {
        copySelectedCellValues({ rangeEndCell, selectedCell, table });
      });
      return () => {
        Mousetrap.unbind(['ctrl+c', 'command+c']);
      };
    }
  }, [selectedCell, rangeEndCell, table]);

  useEffect(() => {
    // when the sort changes, cell selection is cleared and updates the download data with the sorted table data (only for Shopper Metrics .ppt)
    clearSelectedCells();
    if (setDownloadData) {
      setDownloadData((prevData) => ({
        ...prevData,
        table: {
          columns,
          data: rows.map((row) => row.original),
        },
      }));
    }
  }, [sorting, columns, setDownloadData, rows]);

  useOutsideClick({
    onCallback: () => {
      clearSelectedCells();
    },
    ref: tableRef,
  });

  useEffect(() => {
    if (!stickyHeader) return;

    const headerEl = stickyHeaderRef.current;
    const tableEl = tableRef.current;

    if (!headerEl || !tableEl) return;

    const handleScroll = () => {
      headerEl.scrollLeft = tableEl.scrollLeft;
    };

    tableEl.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      tableEl.removeEventListener('scroll', handleScroll);
    };
  }, [stickyHeader]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (useWindowScroll) return;
    if (!tableRef.current && !stickyHeaderRef.current) return;

    let rafId;
    let attempts = 0;
    const measure = () => {
      attempts += 1;
      const headerRow =
        stickyHeaderRef.current?.querySelector(`.${styles['table-data-tr']}`) ??
        tableRef.current?.querySelector(
          `.${styles['table-data-thead']} .${styles['table-data-tr']}`
        );

      if (!headerRow) {
        if (attempts >= 10) return;
        rafId = window.requestAnimationFrame(measure);
        return;
      }

      const next = headerRow.getBoundingClientRect().height;
      if (!next || Number.isNaN(next)) return;

      setHeaderHeight((prev) => (Math.abs(prev - next) <= 1 ? prev : next));
    };

    rafId = window.requestAnimationFrame(measure);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [data, stickyHeader, rowHeight, useWindowScroll, transformedColumns, shouldTruncateHeaders]);

  const calculatedHeight = useMemo(
    () => rowCount * rowHeight + headerHeight,
    [rowCount, rowHeight, headerHeight]
  );

  const virtuosoStyle = useWindowScroll
    ? { minHeight: `${calculatedHeight}px` }
    : { height: `${calculatedHeight}px` };

  const selectedRange = useMemo(
    () =>
      getSelectedCellRange({
        endCell: rangeEndCell ?? selectedCell,
        startCell: selectedCell,
        table,
      }),
    [rangeEndCell, selectedCell, table]
  );

  return (
    <div className={styles['table-data']}>
      <div
        className={classNames({
          [styles['table-data-actions-wrapper']]: prescriptText,
        })}
      >
        {prescriptText}
        <div className={styles['table-data-actions']}>
          {actionButtons?.length > 0 && actionButtons}
          {copyEnabled && (
            <CopyToClipboard
              text="Copy Table"
              value={getCopyTableValue({
                columns: transformedColumns,
                data: rows.map((row) => row.original),
              })}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <>
          <div className={styles['table-data-thead']}>
            <TableHeader table={table} />
          </div>
          <div>
            {[...Array(size)].map((_, index) => (
              <div className={styles['table-data-tr-loading']} key={`row-${index}`}>
                <LoaderSkeleton height={19.5} width={1280}>
                  <rect height="19.5" rx="2" ry="2" width="100%" x="0" y="0" />
                </LoaderSkeleton>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {stickyHeader ? (
            <div className={styles['table-data-header-container']} ref={stickyHeaderRef}>
              <TableHeader shouldTruncateHeaders={shouldTruncateHeaders} table={table} />
            </div>
          ) : null}

          <TableVirtuoso
            components={tableComponents}
            context={{ tableRef }}
            data={rows}
            fixedFooterContent={() => <TableFooter table={table} />}
            fixedHeaderContent={
              !stickyHeader
                ? () => <TableHeader shouldTruncateHeaders={shouldTruncateHeaders} table={table} />
                : undefined
            }
            itemContent={(index) => {
              const row = rows[index];
              if (!row) return null;

              row.index = index;

              return row.getVisibleCells().map((cell) => {
                const gradientBackgroundColor = cell.column.columnDef.gradient
                  ? `rgba(0, 227, 107, ${cell.getValue() / 2})`
                  : null;
                const allowCellWrap = Boolean(cell.column.columnDef?.meta?.allowCellWrap);
                let highlight = false;

                if (typeof cell.column.columnDef.getCellHighlight === 'function') {
                  highlight = cell.column.columnDef.getCellHighlight({
                    rowIndex: row.index,
                    value: cell.getValue(),
                  });
                }

                return (
                  <div
                    className={classNames(styles['table-data-td'], {
                      [styles['has-cell-wrap']]: allowCellWrap,
                      [styles['is-active']]: selectedCell?.id === cell?.id,
                      [styles['is-selected']]: selectedRange[cell?.id],
                      [styles[`is-${highlight}`]]: highlight,
                    })}
                    key={cell.id}
                    onMouseDown={() => {
                      setSelectedCell(cell);
                      setRangeEndCell(cell);
                    }}
                    onMouseEnter={(e) => {
                      if (e.buttons === 1 && selectedCell) {
                        setRangeEndCell(cell);
                      }
                    }}
                    style={{
                      backgroundColor: gradientBackgroundColor,
                      boxSizing: 'border-box',
                      flex: `${cell.column.getSize() ?? 120} 0 auto`,
                      width: cell.column.getSize(),
                    }}
                  >
                    <div
                      className={classNames(styles['table-data-cell-content'], {
                        [styles['allow-cell-wrap']]: allowCellWrap,
                      })}
                    >
                      {hasPlaceholders && cell.column.getIndex() !== 0 ? (
                        <LoaderSkeleton
                          className={styles['table-data-cell-value-placeholder']}
                          height={12}
                          isAnimated={false}
                          width={90}
                        >
                          <rect height="12" rx="0" ry="0" width="90" x="0" y="0" />
                        </LoaderSkeleton>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </div>
                  </div>
                );
              });
            }}
            style={virtuosoStyle}
            useWindowScroll={useWindowScroll}
          />
        </>
      )}
      {isUpdating && (
        <div className={styles['table-data-loader']}>
          <Loader />
        </div>
      )}
      {!isLoading && !isUpdating && rows.length === 0 && (
        <div className={styles['table-data-empty-message']}>{renderEmptyMessage()}</div>
      )}
    </div>
  );
};

const TableFooter = ({ table }) => {
  if (!table) return null;

  const footerGroups = table.getFooterGroups();
  const hasAnyFooter = table
    .getAllColumns()
    .some((col) => col.columnDef.footer !== undefined && col.columnDef.footer !== null);
  if (!hasAnyFooter) return null;

  return (
    <>
      {footerGroups.map((footerGroup) => (
        <div className={styles['table-data-tr']} key={footerGroup.id}>
          {footerGroup.headers.map((header, index) => (
            <div
              className={styles['table-data-tf']}
              key={header.id}
              style={{
                boxSizing: 'border-box',
                flex: index === 0 ? '0 0 250px' : `${header.column.getSize() ?? 120} 0 auto`,
                width: index === 0 ? '250px' : header.column.getSize(),
              }}
            >
              {flexRender(header.column.columnDef.footer, header.getContext())}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

const TableHeader = ({ shouldTruncateHeaders, table }) => {
  if (!table?.getHeaderGroups) return null;

  const headerGroups = table.getHeaderGroups();
  if (!headerGroups?.length) return null;

  return (
    <div className={styles['table-data-thead']}>
      {headerGroups.map((headerGroup) => (
        <div className={styles['table-data-tr']} key={headerGroup.id}>
          {headerGroup.headers?.map((header) => (
            <div
              aria-sort={
                header.column.getIsSorted() === 'desc'
                  ? 'descending'
                  : header.column.getIsSorted() === 'asc'
                    ? 'ascending'
                    : 'none'
              }
              className={classNames(styles['table-data-th'], header.column.className, {
                [styles['is-sortable']]: header.column?.getCanSort(),
              })}
              key={header.id}
              onClick={header.column.getToggleSortingHandler()}
              role="columnheader"
              style={{
                boxSizing: 'border-box',
                flex: `${header.column.getSize() ?? 120} 0 auto`,
                width: header.column.getSize(),
              }}
              tabIndex={header.column?.getCanSort() ? 0 : -1}
              title={flexRender(header.column.columnDef.header, header.getContext())}
            >
              <p
                className={classNames({
                  [styles['table-data-wrap-header']]: !shouldTruncateHeaders,
                })}
              >
                <OverlayTriggerTooltip
                  content={header.column.columnDef.headerTooltip}
                  placement="top"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.index === 0 && ` (${table.getRowModel().rows.length})`}
                </OverlayTriggerTooltip>
              </p>
              <SortIndicator
                direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                isActive={!!header.column.getIsSorted()}
                isDisabled={!header.column.getCanSort()}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const createTableComponents = (tableId) => ({
  FillerRow: ({ height }) => (
    <div>
      <div style={{ height }}></div>
    </div>
  ),
  Table: ({ context: { tableRef }, ...props }) => (
    <div className={styles['table-data-table']} {...props} ref={tableRef} />
  ),
  TableBody: forwardRef(function TableBody(props, ref) {
    const tbody = useScrollSync(tableId);
    useImperativeHandle(ref, () => tbody.current);
    return <div className={styles['table-data-tbody']} {...props} ref={tbody} />;
  }),
  TableFoot: forwardRef(function TableFoot(props, ref) {
    return <div className={styles['table-data-tfoot']} {...props} ref={ref} />;
  }),
  TableHead: forwardRef(function TableHead(props, ref) {
    const thead = useScrollSync(tableId);
    useImperativeHandle(ref, () => thead.current);
    return <div className={styles['table-data-thead']} {...props} ref={thead} />;
  }),

  TableRow: ({ item, ...props }) => {
    const isHighlighted = item?.original?.row_variant === 'highlight';

    return (
      <div
        className={classNames(styles['table-data-tr'], {
          [styles['is-highlighted']]: isHighlighted,
        })}
        {...props}
        key={item.id}
      />
    );
  },
});

TableData.defaultProps = {
  copyEnabled: true,
  renderEmptyMessage: () => {},
  size: 15,
  useWindowScroll: true,
};

TableData.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.node),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Cell: PropTypes.func,
      disableSortBy: PropTypes.bool,
      format: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
      meta: PropTypes.shape({
        allowCellWrap: PropTypes.bool,
      }),
    })
  ).isRequired,
  copyEnabled: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasPlaceholders: PropTypes.bool,
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  prescriptText: PropTypes.node,
  renderEmptyMessage: PropTypes.func,
  rowHeight: PropTypes.number,
  setDownloadData: PropTypes.func,
  shouldTruncateHeaders: PropTypes.bool,
  size: PropTypes.number,
  stickyHeader: PropTypes.bool,
  useWindowScroll: PropTypes.bool,
};

export default TableData;
